import { useCallback, useEffect, useRef, useState } from 'react';

import styled from '@emotion/styled';
import { dropdownMenu2Classes, type PropsWithClassName } from '@kc-console/common-ui';
import { useScopedLinkBuilder } from '@kc-console/context';
import { SpriteSvgIcon } from '@kc-console/shared-ui';

import { FavoriteMenuDropGapDivider } from './FavoriteDropGapDivider';
import FavoriteItemMenu from './FavoriteItemMenu';
import { MoreItemServiceButton } from './MoreItemServiceButton';
import { useFavoriteItemDnd } from './useFavoriteItemDnd';

import type { MouseEvent } from 'react';
import type { FavoriteService } from '@kc-console/context';
import type { DropSide } from './constants';

interface FavoriteMoreItemProps extends PropsWithClassName {
  index: number;
  onDragEnd: (didDrop: boolean) => void;
  onDragHover: (from: number, to: number, side: DropSide) => void;
  service: FavoriteService;
  /** 드롭 indicator 위치. 항목 내부에 in-flow Divider로 표시한다 (배열 splice 금지 — FavoriteMenuDropGapDivider 참고) */
  insertLine?: DropSide | null;
  onContextMenuClose?: () => void;
  onContextMenuOpen?: () => void;
  onServiceClick?: () => void;
}

/**
 * 더보기 영역 즐겨찾기 서비스 아이템
 * @description
 * - 즐겨찾기 서비스를 표시하고 클릭 시 해당 서비스로 이동
 * - 우클릭 시 즐겨찾기 제거 메뉴를 표시
 * - 드래그하여 순서를 변경 (drop은 메뉴 컨테이너가 처리, 아이템은 hover로 삽입 위치만 결정)
 */
const FavoriteMoreItem = ({
  service,
  index,
  insertLine,
  onDragHover,
  onDragEnd,
  onServiceClick,
  onContextMenuOpen,
  onContextMenuClose,
}: FavoriteMoreItemProps) => {
  const internalRef = useRef<HTMLButtonElement>(null);
  const { getProps } = useScopedLinkBuilder();
  // TODO(navigation): ServiceButton(=ButtonGhost) 이 anchor item 지원하도록 개선되면
  //   isAnchor 옵션 제거하고 href 로 전환. 현재 isAnchor:false 는 ctrl/cmd+click 새 탭만
  //   emulate 하며 shift→새 창, alt→다운로드, middle-click 은 지원하지 못한다.
  const linkProps = getProps(service.path, { isAnchor: false });

  const [isOpenMenu, setIsOpenMenu] = useState(false);

  // 드래그 소스 + hover(세로 midpoint) — drop은 메뉴 컨테이너(FavoriteMoreList)에 위임
  const { connectDnd, isDragging } = useFavoriteItemDnd({
    axis: 'y',
    index,
    onDragEnd,
    onDragHover,
    service,
    sourceArea: 'menu',
  });

  const handleButtonRef = useCallback(
    (element: HTMLButtonElement | null) => {
      // internalRef는 컨텍스트 메뉴(FavoriteItemMenu) anchorEl용으로 유지
      internalRef.current = element;
      connectDnd(element);
    },
    [connectDnd],
  );

  const handleRemoveFavoriteMenuOpen = (e: MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    setIsOpenMenu(true);
    onContextMenuOpen?.();
  };

  const handleMenuClose = () => {
    setIsOpenMenu(false);
    onContextMenuClose?.();
  };

  useEffect(() => {
    return () => {
      setIsOpenMenu(false);
    };
  }, []);

  return (
    <Root>
      {insertLine === 'before' && <FavoriteMenuDropGapDivider />}
      <MoreItemServiceButton
        ref={handleButtonRef}
        onClick={e => {
          linkProps.onClick(e);
          onServiceClick?.();
        }}
        onContextMenu={e => handleRemoveFavoriteMenuOpen(e)}
        isDragging={isDragging}
        startIcon={
          <SpriteSvgIcon fontSize={16} id={service.keyword} category={'BeyondServiceRegular'} />
        }
      >
        {service.name}
      </MoreItemServiceButton>
      {insertLine === 'after' && <FavoriteMenuDropGapDivider />}
      <FavoriteItemMenu
        service={service}
        open={isOpenMenu}
        onClose={handleMenuClose}
        anchorEl={internalRef.current}
        placement="left-start"
        css={{
          [`.${dropdownMenu2Classes.paper}`]: {
            width: 160,
          },
        }}
      />
    </Root>
  );
};

export default FavoriteMoreItem;

const Root = styled('div')`
  width: 100%;
`;
